import { useState } from 'react';
import { triggerEnhancedWebviewFeature } from '@buzzfeed/bf-utils/lib/webview';
import { followUser, unfollowUser } from '../../../utils/getUsers';
import Spinner from './Spinner';
import styles from './index.module.scss';
import buttonStyles from '../../../styles/buttons.module.scss';

export default function UserCard({ user, onFollowChange }) {
  const { user_id, display_name, username, avatar: avatarUrl, is_following } = user;
  const displayName = display_name || username || 'Unknown User';
  
  const [isLoading, setIsLoading] = useState(false);
  
  // Optimize avatar URL - use small size for thumbnails
  const optimizedAvatarUrl = avatarUrl 
    ? (() => {
        // Parse URL to rebuild with optimized parameters
        const url = new URL(avatarUrl);
        // Replace/set query parameters for optimization
        url.searchParams.set('output-format', 'webp');
        url.searchParams.set('output-quality', '50');
        url.searchParams.set('resize', '64:64');
        return url.toString();
      })()
    : null;
  
  const handleUserClick = () => {
    if (!user_id) return;

    triggerEnhancedWebviewFeature({
      feature: 'navigate',
      params: { path: `/profile/${user_id}` },
      version: 1,
    });
  };

  const handleFollowClick = async (e) => {
    e.stopPropagation();
    if (isLoading) return;
    
    setIsLoading(true);
    const action = is_following ? 'unfollow' : 'follow';
    
    // Make API call first
    const result = is_following 
      ? await unfollowUser(user_id)
      : await followUser(user_id);
    
    if (result.error) {
      triggerEnhancedWebviewFeature({
        feature: 'showToast',
        params: { 
          message: `Failed to ${action} user. Please try again.`,
          duration: 'short'
        },
        version: 1,
      });
    } else {
      // Update UI only on success
      const newFollowState = !is_following;
      if (onFollowChange) {
        onFollowChange(user_id, newFollowState);
      }
    }
    
    setIsLoading(false);
  };

  return (
    <div className={styles.userCard} onClick={handleUserClick}>
      <div 
        className={styles.avatar}
        style={optimizedAvatarUrl ? { backgroundImage: `url(${optimizedAvatarUrl})` } : undefined}
        aria-label={`${displayName}'s avatar`}
      />
      
      <div className={styles.info}>
        <div className={styles.displayName}>{displayName}</div>
        {username && <div className={styles.username}>@{username}</div>}
      </div>

      <button 
        className={is_following ? buttonStyles.darkGrayButton : buttonStyles.orangeButton}
        onClick={handleFollowClick}
        disabled={isLoading}
        aria-label={is_following ? `Unfollow ${displayName}` : `Follow ${displayName}`}
      >
        {isLoading ? (
          <Spinner size={20} color={is_following ? '#fff' : '#000'} />
        ) : (
          is_following ? 'Unfollow' : 'Follow'
        )}
      </button>
    </div>
  );
}
